import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAnnouncement,
  deleteAnnouncement,
  dismissAnnouncement,
  getActiveAnnouncements,
  getAnnouncementImageBlob,
  listAnnouncements,
  setAnnouncementActive,
} from '../api/endpoints/announcements';

const ANNOUNCEMENTS_KEY = ['announcements'];
const ACTIVE_ANNOUNCEMENTS_KEY = ['announcements', 'active'];
// Not time-critical like alarms — long enough to not hammer the API, short enough that a
// notice posted while someone's tab is already open still reaches them before too long.
const ACTIVE_REFETCH_MS = 5 * 60_000;

/** Admin-only management list (GET /announcements). */
export function useAnnouncements() {
  return useQuery({ queryKey: ANNOUNCEMENTS_KEY, queryFn: listAnnouncements });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ title, image, expiresAt }: { title: string; image: File; expiresAt?: string }) =>
      createAnnouncement(title, image, expiresAt),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_KEY }),
  });
}

export function useSetAnnouncementActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setAnnouncementActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_KEY }),
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_KEY }),
  });
}

/** Drives the login popup: active announcements the current user hasn't dismissed yet. */
export function useActiveAnnouncements() {
  return useQuery({ queryKey: ACTIVE_ANNOUNCEMENTS_KEY, queryFn: getActiveAnnouncements, refetchInterval: ACTIVE_REFETCH_MS });
}

export function useDismissAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissAnnouncement(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACTIVE_ANNOUNCEMENTS_KEY }),
  });
}

/** A plain <img src="/api/.../image"> can't carry the Authorization header the backend
 *  requires, so this fetches the image as a blob through the authenticated apiClient and
 *  hands back an object URL, revoked on unmount/id change. Used for both the popup's full
 *  image and the admin list's thumbnail. */
export function useAnnouncementImage(id: string | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!id) {
      setUrl(undefined);
      return;
    }
    let objectUrl: string | undefined;
    let cancelled = false;
    void getAnnouncementImageBlob(id).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return url;
}
